import type { CaptureResult, Properties } from "posthog-js";
import { ALL_APP_LOCALES } from "@/lib/i18n/constants";
import type { PostHogReleaseProperties } from "@/lib/posthog/release";

const PAGE_NOT_FOUND_ROUTE_PATHS = new Set(["/missing", "/missing/missing", "/missing/deep"]);
const PAGE_NOT_FOUND_REFERRER_BUCKETS = new Set(["external", "internal", "none"]);
const PAGE_NOT_FOUND_SAMPLING_TIERS = new Set(["bot_probe", "standard"]);
const PAGE_NOT_FOUND_LOCALES = new Set<string>(ALL_APP_LOCALES);
const POSTHOG_INTERNAL_TOKEN_KEYS = ["token", "api_key"] as const;
const POSTHOG_INTERNAL_ID_KEYS = [
  "distinct_id",
  "$device_id",
  "$session_id",
  "$window_id",
  "$insert_id",
  "$pageview_id",
] as const;
const POSTHOG_OPAQUE_ID_PATTERN = /^[a-z0-9:_-]{1,200}$/i;
const POSTHOG_LIB_VERSION_PATTERN = /^\d+\.\d+\.\d+(?:[-+][a-z0-9.-]+)?$/i;
const POSTHOG_CONFIG_DEFAULTS_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const SAFE_RELEASE_VALUE_PATTERN = /^[a-z0-9][a-z0-9/_.:-]{0,119}$/i;
const SAFE_GIT_COMMIT_PATTERN = /^(?=.*[a-f])[a-f0-9]{7,64}$/i;
const RELEASE_PROPERTY_KEYS = [
  "deploy_environment",
  "deployment_id",
  "git_branch",
  "git_commit_sha",
  "release_id",
] as const;

const isSafeOpaqueId = (value: unknown): value is string =>
  typeof value === "string" && POSTHOG_OPAQUE_ID_PATTERN.test(value);

const isSafeReleaseValue = (key: (typeof RELEASE_PROPERTY_KEYS)[number], value: unknown): value is string => {
  if (typeof value !== "string") {
    return false;
  }

  if (key === "git_commit_sha") {
    return SAFE_GIT_COMMIT_PATTERN.test(value);
  }

  return SAFE_RELEASE_VALUE_PATTERN.test(value) && /[a-z]/i.test(value);
};

const copySafeReleaseProperty = (
  source: Partial<Record<(typeof RELEASE_PROPERTY_KEYS)[number], unknown>>,
  target: Properties,
  key: (typeof RELEASE_PROPERTY_KEYS)[number],
) => {
  const value = source[key];

  if (isSafeReleaseValue(key, value)) {
    target[key] = value;
  }
};

/**
 * Rebuilds the final SDK-merged page_not_found envelope from a strict allowlist.
 * Any registered, session-entry, acquisition, URL, query, referrer, demo, or
 * person property added by PostHog after capture() is intentionally discarded.
 */
export function scrubPageNotFoundCaptureResult(
  captureResult: CaptureResult,
  expectedToken: string,
  trustedReleaseProperties: PostHogReleaseProperties = {},
): CaptureResult | null {
  if (
    captureResult.event !== "page_not_found" ||
    !isSafeOpaqueId(captureResult.uuid) ||
    typeof expectedToken !== "string" ||
    expectedToken.length === 0
  ) {
    return null;
  }

  const source = captureResult.properties;

  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }

  const safeProperties: Properties = {};
  let hasExpectedToken = false;

  for (const key of POSTHOG_INTERNAL_TOKEN_KEYS) {
    if (source[key] === expectedToken) {
      safeProperties[key] = expectedToken;
      hasExpectedToken = true;
    }
  }

  if (!hasExpectedToken || !isSafeOpaqueId(source.distinct_id)) {
    return null;
  }

  for (const key of POSTHOG_INTERNAL_ID_KEYS) {
    const value = source[key];

    if (isSafeOpaqueId(value)) {
      safeProperties[key] = value;
    }
  }

  if (source.$lib === "web") {
    safeProperties.$lib = source.$lib;
  }

  if (typeof source.$lib_version === "string" && POSTHOG_LIB_VERSION_PATTERN.test(source.$lib_version)) {
    safeProperties.$lib_version = source.$lib_version;
  }

  if (typeof source.$process_person_profile === "boolean") {
    safeProperties.$process_person_profile = source.$process_person_profile;
  }

  if (typeof source.$is_identified === "boolean") {
    safeProperties.$is_identified = source.$is_identified;
  }

  if (typeof source.$config_defaults === "string" && POSTHOG_CONFIG_DEFAULTS_PATTERN.test(source.$config_defaults)) {
    safeProperties.$config_defaults = source.$config_defaults;
  }

  if (typeof source.$time === "number" && Number.isFinite(source.$time) && source.$time >= 0) {
    safeProperties.$time = source.$time;
  }

  if (typeof source.locale === "string" && PAGE_NOT_FOUND_LOCALES.has(source.locale)) {
    safeProperties.locale = source.locale;
  }

  if (typeof source.route_path === "string" && PAGE_NOT_FOUND_ROUTE_PATHS.has(source.route_path)) {
    safeProperties.route_path = source.route_path;
  }

  if (typeof source.referrer_bucket === "string" && PAGE_NOT_FOUND_REFERRER_BUCKETS.has(source.referrer_bucket)) {
    safeProperties.referrer_bucket = source.referrer_bucket;
  }

  if (typeof source.sampling_tier === "string" && PAGE_NOT_FOUND_SAMPLING_TIERS.has(source.sampling_tier)) {
    safeProperties.sampling_tier = source.sampling_tier;
  }

  for (const key of RELEASE_PROPERTY_KEYS) {
    copySafeReleaseProperty(trustedReleaseProperties, safeProperties, key);
  }

  if (!safeProperties.locale || !safeProperties.route_path || !safeProperties.referrer_bucket || !safeProperties.sampling_tier) {
    return null;
  }

  return {
    uuid: captureResult.uuid,
    event: "page_not_found",
    properties: safeProperties,
    ...(captureResult.timestamp instanceof Date && Number.isFinite(captureResult.timestamp.getTime())
      ? { timestamp: captureResult.timestamp }
      : {}),
  };
}
